import { VideoRemakeRequestVideoSource } from "@apis/generated/openapi";
import { classifyReferenceVideoIdentity, type ReferenceVideoIdentity } from "@apis/videoRemakeRequest";
import type { ReferenceVideoLibraryItem } from "@pages/videoRemake/ReferenceVideoCard";
import type { ReferenceVideoPickPayload } from "@pages/videoRemake/ReferenceVideoModal";
import type { VideoLinkPreviewData } from "@pages/videoRemake/useReferenceVideoLink";

/** 参考视频侧栏预览用的附加信息（不参与请求体构造） */
interface ReferenceVideoPreview {
  /** 参考视频封面，主要用于侧栏预览 */
  coverUrl?: string;
  /** 参考视频时长（秒），用于积分预览 */
  durationSeconds?: number;
}

/**
 * Studio 内联绑定的「参考视频」信息 = 互斥身份（{@link ReferenceVideoIdentity}）+ 预览附加信息。
 *
 * - 通过 ReferenceVideoModal「Video link / Video file / Library」入口注入，或 Kalodata 跨域跳转的 URL query 注入。
 */
export type ReferenceVideoState = ReferenceVideoIdentity & ReferenceVideoPreview;

const SUPPORTED_VIDEO_SOURCES = new Set<string>(Object.values(VideoRemakeRequestVideoSource));

const toVideoSource = (value: string | undefined): VideoRemakeRequestVideoSource | undefined => {
  if (!value) return undefined;
  return SUPPORTED_VIDEO_SOURCES.has(value) ? (value as VideoRemakeRequestVideoSource) : undefined;
};

/** 根据链接 host 推断 PRD 支持的外链平台（仅作 link-preview 缺字段时的兜底） */
const detectVideoSource = (url: string): VideoRemakeRequestVideoSource | undefined => {
  const lower = url.toLowerCase();
  if (lower.includes("tiktok.com")) return VideoRemakeRequestVideoSource.TikTok;
  if (lower.includes("kalodata.com")) return VideoRemakeRequestVideoSource.Kalodata;
  if (lower.includes("youtube.com") || lower.includes("youtu.be")) return VideoRemakeRequestVideoSource.YouTube;
  return undefined;
};

const readField = <T = string>(source: Record<string, unknown>, ...keys: string[]): T | undefined => {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value as T;
  }
  return undefined;
};

const normalizeLinkPreview = (preview: VideoLinkPreviewData): ReferenceVideoState | null => {
  const videoUrl = preview.originVideoLink?.trim();
  if (!videoUrl) return null;

  const videoSource = toVideoSource(preview.videoSource) ?? detectVideoSource(videoUrl);
  if (!videoSource) return null;

  return {
    kind: "link",
    videoUrl,
    videoId: preview.videoId,
    videoSource,
    coverUrl: preview.coverImageUrl,
  };
};

/**
 * 把 ReferenceVideoModal 选中结果归一化成 ReferenceVideoState；用于 Welcome → Studio 跳转的 navigate state。
 */
export const normalizeReferencePayload = (payload: ReferenceVideoPickPayload): ReferenceVideoState | null => {
  if (!payload) return null;

  if (payload.source === "videoLink") {
    const preview = payload.data as VideoLinkPreviewData | null;
    if (!preview || typeof preview !== "object") return null;
    return normalizeLinkPreview(preview);
  }

  if (payload.source === "videoFile") {
    const file = payload.data as Record<string, unknown> | null;
    if (!file) return null;
    const response = (file.response ?? {}) as Record<string, unknown>;
    const videoUrl = (file.url as string | undefined) ?? readField<string>(response, "videoUrl") ?? "";
    const identity = classifyReferenceVideoIdentity({
      videoUrl,
      videoAssetId: readField<number>(response, "assetId"),
      videoSource: VideoRemakeRequestVideoSource.Kaloclip,
    });
    if (!identity) return null;
    return {
      ...identity,
      coverUrl: readField<string>(response, "coverImageUrl"),
      durationSeconds: readField<number>(response, "duration"),
    };
  }

  const selectedVideo = (payload.data ?? {}) as ReferenceVideoLibraryItem;
  const item = ((selectedVideo.raw ?? payload.data) as Record<string, unknown>) ?? {};
  const vidExtra = (item.vidExtra as Record<string, unknown> | undefined) ?? {};
  const context = ((item.jobItem as Record<string, unknown> | undefined)?.context as Record<string, unknown>) ?? {};
  const videoUrl =
    selectedVideo.videoUrl ??
    readField<string>(item, "url", "videoUrl") ??
    readField<string>(context, "videoUrl") ??
    "";
  const coverUrl =
    selectedVideo.coverUrl ?? readField<string>(item, "coverImageUrl") ?? readField<string>(vidExtra, "coverImageUrl");
  const durationSeconds = readField<number>(item, "duration") ?? readField<number>(vidExtra, "duration");

  if (selectedVideo.variant === "saved") {
    const videoSource =
      readField<VideoRemakeRequestVideoSource>(item, "videoSource") ??
      readField<VideoRemakeRequestVideoSource>(context, "videoSource") ??
      detectVideoSource(videoUrl);

    const identity = classifyReferenceVideoIdentity({
      videoUrl,
      videoId: readField<string>(item, "videoId") ?? readField<string>(context, "videoId"),
      videoSource,
    });
    if (!identity) return null;

    return { ...identity, coverUrl, durationSeconds };
  }

  const identity = classifyReferenceVideoIdentity({
    videoUrl,
    videoAssetId: readField<number>(item, "id", "assetId"),
    videoSource: VideoRemakeRequestVideoSource.Kaloclip,
  });
  if (!identity) return null;

  return { ...identity, coverUrl, durationSeconds };
};

/**
 * 当前 Studio 商品来源标记，用于 PRD 4.1 中
 * 「Save Product Entity」checkbox 的展示规则：
 *
 * - `link` / `manual`：商品不在收藏夹中 → 展示 checkbox，默认勾选可取消
 * - `library-created` / `library-saved`：商品已在收藏夹中 → 隐藏 checkbox
 * - `null`：尚未选择任何商品
 */
export type ProductOrigin = "link" | "manual" | "library-created" | "library-saved" | null;
